/**
 * Type Definitions for Contract Termination Feature
 * US-CONT-006: Terminate Contract
 */

// ============================================
// ENUMS
// ============================================

/**
 * Termination reason options
 */
export const TerminationReasonMap = {
  NON_PAYMENT: 'NON_PAYMENT',
  CONTRACT_VIOLATION: 'CONTRACT_VIOLATION',
  CUSTOMER_REQUEST: 'CUSTOMER_REQUEST',
  BUSINESS_CLOSURE: 'BUSINESS_CLOSURE',
  OTHER: 'OTHER'
} as const;

export type TerminationReason = keyof typeof TerminationReasonMap;

/**
 * User-friendly labels for termination reasons
 */
export const TERMINATION_REASON_LABELS: Record<TerminationReason, string> = {
  NON_PAYMENT: 'Non-payment',
  CONTRACT_VIOLATION: 'Contract Violation',
  CUSTOMER_REQUEST: 'Customer Request',
  BUSINESS_CLOSURE: 'Business Closure',
  OTHER: 'Other'
};

/**
 * Descriptions for termination reasons
 */
export const TERMINATION_REASON_DESCRIPTIONS: Record<TerminationReason, string> = {
  NON_PAYMENT: 'Customer failed to make timely payments',
  CONTRACT_VIOLATION: 'Customer violated terms and conditions of the contract',
  CUSTOMER_REQUEST: 'Customer voluntarily requested contract termination',
  BUSINESS_CLOSURE: 'Customer business has ceased operations',
  OTHER: 'Other reason (requires detailed explanation)'
};

// ============================================
// INTERFACES
// ============================================

/**
 * Request payload for contract termination
 */
export interface TerminateContractRequest {
  contractId: string;
  reason: TerminationReason;
  notes: string;
  authorizationCode?: string; // Phase 2: Manager authorization
}

/**
 * Response from contract termination API
 */
export interface TerminateContractResponse {
  success: boolean;
  contractId: string;
  contractNumber: string;
  terminationDate: string; // ISO 8601
  terminatedBy: string;
  reason: TerminationReason;
  notes: string;
  message: string;
}

/**
 * Termination history entry
 */
export interface TerminationHistory {
  id: string;
  contractId: string;
  contractNumber: string;
  terminationDate: string; // ISO 8601
  terminatedBy: string;
  terminatedByName?: string;
  reason: TerminationReason;
  notes: string;
  customerNotified: boolean;
  notificationDate?: string; // ISO 8601
}

/**
 * Form validation errors
 */
export interface TerminationFormErrors {
  reason?: string;
  notes?: string;
  authorizationCode?: string;
}

/**
 * Form state for termination dialog
 */
export interface TerminationFormState {
  reason: TerminationReason | '';
  notes: string;
  authorizationCode?: string;
}

/**
 * Validation result
 */
export interface ValidationResult {
  isValid: boolean;
  errors: TerminationFormErrors;
}

/**
 * Termination permission check result
 */
export interface TerminationPermission {
  canTerminate: boolean;
  reason?: string;
  requiresAuthorization?: boolean;
}

/**
 * API Response wrapper for termination
 */
export interface TerminationApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  timestamp?: string;
}

/**
 * Notification payload for customer
 */
export interface TerminationNotification {
  contractId: string;
  customerEmail?: string;
  customerPhone?: string;
  customerName: string;
  contractNumber: string;
  terminationDate: string;
  reason: TerminationReason;
  message: string;
}

// ============================================
// CONSTANTS
// ============================================

/**
 * Minimum character length for notes
 */
export const MIN_NOTES_LENGTH = 20;

/**
 * Maximum character length for notes
 */
export const MAX_NOTES_LENGTH = 1000;

/**
 * Default termination form state
 */
export const DEFAULT_TERMINATION_FORM_STATE: TerminationFormState = {
  reason: '',
  notes: '',
  authorizationCode: ''
};
